import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, ScrollView, Linking } from 'react-native';
import {
  Bevel,
  Button,
  Checkbox,
  ProgressBar,
  RunningMan,
  Text,
  TextField,
  Window,
  colors,
  isDesktop,
  makeStyles,
  useTheme,
} from '@ogaim/ui';
import { useStore } from '../store';
import { play } from '../sounds';

export interface ScreenProps {
  /**
   * Whether to draw the Win95 window frame. The web desktop supplies its own
   * draggable frame and taskbar entry, so it renders these screens frameless.
   */
  frame?: boolean;
}

/**
 * The AIM 5.x sign-on window: running man over the screen-name field, the two
 * checkboxes, and the Setup / Help / Sign On row.
 */
export function SignOnScreen({ frame = true }: ScreenProps = {}) {
  const styles = useStyles();
  const signIn = useStore((s) => s.signIn);
  const goTo = useStore((s) => s.goTo);
  const error = useStore((s) => s.error);
  const clearError = useStore((s) => s.clearError);
  const signingOn = useStore((s) => s.signingOn);
  const progress = useStore((s) => s.signOnProgress);

  const [screenName, setScreenName] = useState('');
  const [password, setPassword] = useState('');
  const [savePassword, setSavePassword] = useState(false);
  const passwordRef = useRef<any>(null);

  const canSubmit = screenName.trim().length >= 3 && password.length > 0 && !signingOn;

  useEffect(() => {
    if (error) play('error');
  }, [error]);

  const submit = async () => {
    if (!canSubmit) return;
    try {
      await signIn(screenName, password);
      play('signOn');
    } catch {
      // The store already surfaced the message; keep the user on this screen.
      setPassword('');
    }
  };

  const body = (
    <ScrollView
          contentContainerStyle={styles.body}
      keyboardShouldPersistTaps="handled"
      bounces={false}
    >
          <View style={styles.logoWell}>
            <RunningMan size={44} />
            <Text bold style={styles.wordmark}>
              OG Instant Messenger
            </Text>
          </View>

          <View style={styles.fields}>
            <Text style={styles.fieldLabel}>Screen Name</Text>
            <TextField
              value={screenName}
              onChangeText={(t) => {
                clearError();
                setScreenName(t);
              }}
              editable={!signingOn}
              returnKeyType="next"
              onSubmitEditing={() => passwordRef.current?.focus()}
              textContentType="username"
              maxLength={16}
              accessibilityLabel="Screen Name"
            />

            <Text style={[styles.fieldLabel, styles.fieldLabelSpaced]}>Password</Text>
            <TextField
              ref={passwordRef}
              value={password}
              onChangeText={(t) => {
                clearError();
                setPassword(t);
              }}
              editable={!signingOn}
              secureTextEntry
              returnKeyType="go"
              onSubmitEditing={submit}
              textContentType="password"
              accessibilityLabel="Password"
            />

            <View style={styles.checks}>
              <Checkbox
                checked={savePassword}
                onChange={setSavePassword}
                label="Save password"
                disabled={signingOn}
              />
            </View>
          </View>

          {/* Status well: errors and the sign-on progress share this slot, the
              way the original swapped its status text during connection. */}
          <View style={styles.statusWell}>
            {signingOn ? (
              <>
                <Text style={styles.statusText}>{progressLabel(progress)}</Text>
                <ProgressBar value={progress} />
              </>
            ) : error ? (
              <Text style={styles.errorText}>{error}</Text>
            ) : null}
          </View>

          <View style={styles.actions}>
            <Button
              title="Get a Screen Name"
              onPress={() => goTo('signup')}
              disabled={signingOn}
              style={styles.actionWide}
            />
            <View style={styles.actionRow}>
              <Button
                title="Help"
                onPress={() => Linking.openURL('https://example.invalid/og-aim/help').catch(() => {})}
                disabled={signingOn}
                style={styles.action}
              />
              <Button
                title="Sign On"
                onPress={submit}
                disabled={!canSubmit}
                primary
                style={styles.action}
              />
            </View>
          </View>

          <Bevel variant="thin" style={styles.versionRule} />
          <Text disabled style={styles.version}>
            Version 5.1.3036 · Invitation required
          </Text>
    </ScrollView>
  );

  if (!frame) return body;

  return (
    <View style={styles.backdrop}>
      <Window title="Sign On" style={styles.window} icon={<RunningMan size={14} />}>
        {body}
      </Window>
    </View>
  );
}

/** Mirrors the original's connection chatter rather than showing a bare percentage. */
function progressLabel(p: number): string {
  if (p < 0.3) return 'Connecting to server…';
  if (p < 0.6) return 'Checking password…';
  if (p < 0.8) return 'Starting services…';
  return 'Loading buddy list…';
}

const useStyles = makeStyles((t) => ({
  // Win95 desktop teal, so the window reads as floating on a desktop.
  backdrop: { flex: 1, backgroundColor: t.colors.desktop, alignItems: 'center', justifyContent: 'center', padding: 12 },
  window: { width: '100%', maxWidth: 320, flex: isDesktop ? 0 : 1, maxHeight: 520 },
  body: { padding: 10, gap: 10 },
  logoWell: { alignItems: 'center', gap: 6, paddingVertical: 6 },
  wordmark: { fontSize: 13 },
  fields: {},
  fieldLabel: { marginBottom: 2 },
  fieldLabelSpaced: { marginTop: 8 },
  checks: { marginTop: 8, gap: 4 },
  statusWell: { minHeight: 34, justifyContent: 'center', gap: 4 },
  statusText: {},
  errorText: { color: '#800000' },
  actions: { gap: 6 },
  actionWide: { alignSelf: 'stretch' },
  actionRow: { flexDirection: 'row', gap: 6 },
  action: { flex: 1 },
  versionRule: { marginTop: 4 },
  version: { textAlign: 'center', fontSize: 10 },
}));
